import React, { useEffect, useState } from 'react';
import {
	Box,
	Divider,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	ListItemText,
	Paper,
	Popover,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
	addMonths,
	endOfDay,
	endOfMonth,
	endOfYear,
	format,
	isAfter,
	isBefore,
	isSameDay,
	startOfDay,
	startOfMonth,
	startOfYear,
	subMonths,
} from 'date-fns';

const PRESETS = [
	{ value: 'lastMonth', label: 'Last month' },
	{ value: 'thisMonth', label: 'This month' },
	{ value: 'nextMonth', label: 'Next month' },
	{ value: 'currentYear', label: 'Current year' },
];

const getPresetRange = (preset) => {
	const now = new Date();
	switch (preset) {
		case 'thisMonth':
			return { from: startOfMonth(now), to: endOfMonth(now) };
		case 'lastMonth': {
			const lastMonth = subMonths(now, 1);
			return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
		}
		case 'nextMonth': {
			const nextMonth = addMonths(now, 1);
			return { from: startOfMonth(nextMonth), to: endOfMonth(nextMonth) };
		}
		case 'currentYear':
			return { from: startOfYear(now), to: endOfYear(now) };
		default:
			return { from: null, to: null };
	}
};

const formatLabel = (from, to) => {
	if (!from || !to) return '';
	return `${format(from, 'dd/MM/yyyy')} – ${format(to, 'dd/MM/yyyy')}`;
};

const RangeDay = ({ day, outsideCurrentMonth, draftFrom, draftTo, ...other }) => {
	const dayStart = startOfDay(day);
	const from = draftFrom ? startOfDay(draftFrom) : null;
	const to = draftTo ? startOfDay(draftTo) : null;

	const isStart = Boolean(from && isSameDay(dayStart, from));
	const isEnd = Boolean(to && isSameDay(dayStart, to));
	const isBetween =
		Boolean(from && to) &&
		!outsideCurrentMonth &&
		isAfter(dayStart, from) &&
		isBefore(dayStart, to);
	const showRangeBar =
		Boolean(from && to) &&
		!outsideCurrentMonth &&
		!isSameDay(from, to) &&
		(isStart || isEnd || isBetween);

	return (
		<Box
			sx={{
				width: '100%',
				height: 36,
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				position: 'relative',
				...(showRangeBar && {
					'&::before': {
						content: '""',
						position: 'absolute',
						top: '50%',
						transform: 'translateY(-50%)',
						height: 28,
						left: isStart ? '50%' : 0,
						right: isEnd ? '50%' : 0,
						backgroundColor: 'rgba(80, 90, 252, 0.16)',
						zIndex: 0,
					},
				}),
			}}
		>
			<PickersDay
				{...other}
				day={day}
				outsideCurrentMonth={outsideCurrentMonth}
				selected={isStart || isEnd}
				disableMargin
				sx={(theme) => ({
					position: 'relative',
					zIndex: 1,
					...(isBetween && {
						backgroundColor: 'transparent',
						color: 'text.primary',
						'&:hover': {
							backgroundColor: 'rgba(80, 90, 252, 0.2)',
						},
					}),
					...((isStart || isEnd) && {
						backgroundColor: `${theme.palette.primary.main} !important`,
						color: `${theme.palette.folioWhite} !important`,
						fontWeight: 600,
						'&:hover': {
							backgroundColor: `${theme.palette.primary.main} !important`,
						},
					}),
				})}
			/>
		</Box>
	);
};

RangeDay.displayName = 'RangeDay';

export const PresetDateRangeSelect = ({
	from = null,
	to = null,
	onChange,
	disabled = false,
	placeholder = 'Select Date',
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [draftFrom, setDraftFrom] = useState(from);
	const [draftTo, setDraftTo] = useState(to);

	const open = Boolean(anchorEl);
	const label = formatLabel(from, to);
	const selectingEnd = Boolean(draftFrom && !draftTo);

	useEffect(() => {
		if (!open) return;
		setDraftFrom(from);
		setDraftTo(to);
	}, [open, from, to]);

	const apply = (nextFrom, nextTo) => {
		onChange?.(nextFrom ? startOfDay(nextFrom) : null, nextTo ? endOfDay(nextTo) : null);
		setAnchorEl(null);
	};

	const handlePreset = (preset) => {
		const range = getPresetRange(preset);
		apply(range.from, range.to);
	};

	const handleDaySelect = (date) => {
		if (!date) return;
		const selected = startOfDay(date);

		// First click (or restart after a full range): set start
		if (!draftFrom || (draftFrom && draftTo)) {
			setDraftFrom(selected);
			setDraftTo(null);
			return;
		}

		// Second click: must be on/after start
		if (isBefore(selected, startOfDay(draftFrom))) {
			setDraftFrom(selected);
			setDraftTo(null);
			return;
		}

		apply(draftFrom, selected);
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onChange?.(null, null);
		setAnchorEl(null);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box sx={{ width: '100%' }}>
				<TextField
					fullWidth
					disabled={disabled}
					value={label}
					placeholder={placeholder}
					onClick={(e) => {
						if (!disabled) setAnchorEl(e.currentTarget);
					}}
					inputProps={{ readOnly: true }}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<CalendarMonthOutlinedIcon fontSize='small' color='action' />
							</InputAdornment>
						),
						endAdornment: label ? (
							<InputAdornment position='end'>
								<IconButton size='small' onClick={handleClear} edge='end'>
									<ClearIcon fontSize='small' />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
					sx={{
						'& .MuiInputBase-root': {
							cursor: disabled ? 'default' : 'pointer',
						},
						'& input': {
							cursor: disabled ? 'default' : 'pointer',
							paddingY: '7.5px',
						},
					}}
				/>

				<Popover
					open={open}
					anchorEl={anchorEl}
					onClose={() => setAnchorEl(null)}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				>
					<Paper elevation={0} sx={{ display: 'flex' }}>
						<List dense sx={{ minWidth: 160, py: 1 }}>
							{PRESETS.map((option) => (
								<ListItemButton
									key={option.value}
									onClick={() => handlePreset(option.value)}
								>
									<ListItemText primary={option.label} />
								</ListItemButton>
							))}
						</List>

						<Divider orientation='vertical' flexItem />
						<Stack sx={{ px: 1, pb: 1.5 }}>
							<Typography
								variant='caption'
								sx={{ px: 1.5, pt: 1.5, color: 'text.secondary' }}
							>
								{selectingEnd
									? `Start ${format(draftFrom, 'dd/MM/yyyy')} — select end date`
									: draftFrom && draftTo
										? formatLabel(draftFrom, draftTo)
										: 'Select start date'}
							</Typography>
							<DateCalendar
								value={draftTo || draftFrom}
								onChange={handleDaySelect}
								shouldDisableDate={(date) =>
									selectingEnd
										? isBefore(startOfDay(date), startOfDay(draftFrom))
										: false
								}
								slots={{ day: RangeDay }}
								slotProps={{
									day: {
										draftFrom,
										draftTo,
									},
								}}
							/>
						</Stack>
					</Paper>
				</Popover>
			</Box>
		</LocalizationProvider>
	);
};

PresetDateRangeSelect.displayName = 'PresetDateRangeSelect';
